<template>
  <fieldset class="pdl-swatches">
    <legend class="sr-only">{{ swatchLegend }}</legend>
    <div
      v-for="(option, index) in computedOptions"
      :key="index"
      v-tippy="getToolTip(option)"
      v-bind="additionalAttributes(option)"
      class="pdl-swatch-container"
      @mouseover="onMouseOver(option)"
      @mouseleave="onMouseLeave(option)"
    >
      <label :qaid="`color-swatch-label-${getOptionId(option, index)}`">
        <input
          :id="`${radioName}-swatch-${getOptionId(option, index)}`"
          v-model="active"
          type="radio"
          class="pdl-swatch__radio"
          :data-color="getOptionId(option, index)"
          data-id="#"
          data-url="#"
          :name="radioName"
          :value="option"
          :disabled="isDisplayOnly"
          @change="onChange"
        />
        <span class="sr-only">
          {{ option.labelName || getOptionId(option, index) }}
          {{ !option.isAvailable ? getSwatchUnavailableLabel(option) : '' }}
        </span>
        <swatch
          :is-available="option.isAvailable"
          :is-small="isSmall"
          :is-large="isLarge"
          :first-color="option.firstColor"
          :second-color="option.secondColor"
          :image-url="option.imageUrl"
          :name="option.name"
          :class="cursorClass"
          :label-name="option.labelName || getOptionId(option, index)"
        />
      </label>
    </div>
  </fieldset>
</template>

<script>
import Swatch from './Swatch.vue';

export default {
  name: 'PdlSwatchGroup',

  components: {Swatch},

  props: {
    /**
     * Disables selection of swatches in group.
     */
    isDisplayOnly: {
      type: Boolean,
      default: false,
    },

    swatchLegend: {
      type: String,
      default: 'Select a color',
    },

    /**
     * A callback function to be called when a user hovers over a new swatch that is not currently selected.
     */
    mouseOverCallback: {
      type: Function,
      default: () => {},
    },

    /**
     * A callback function to be called when user stops hovering over a swatch.
     */
    mouseLeaveCallback: {
      type: Function,
      default: () => {},
    },

    /**
     * A callback function to be called when a new swatch is selected.
     */
    changeCallback: {
      type: Function,
      default: () => true,
    },

    /**
     * Pre-selects an option in the given `options` prop array. Optional.
     */
    initialSelectedOption: {
      type: Object,
      default: () => {},
    },

    /**
     * An array of `swatch` options. See 'Swatch' for possible `option` attributes.
     */
    options: {
      type: Array,
      required: true,
    },

    /**
     * The name to be given to the radio group.
     */
    radioName: {
      type: String,
      required: true,
    },

    /**
     * If true, a smaller version of the swatches will be displayed.
     */
    isSmall: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * If true, a larger version of the swatches will be displayed.
     */
    isLarge: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      active: {...this.initialSelectedOption, radioName: this.initialSelectedOption?.radioName ?? this.radioName},
      activeMouseOver: null,
    };
  },

  computed: {
    cursorClass() {
      return this.isDisplayOnly ? 'cursor-default' : 'cursor-pointer';
    },

    computedOptions() {
      return this.options.map((option) => {
        return {...option, radioName: this.radioName};
      });
    },
  },

  watch: {
    initialSelectedOption(newSelectedOption) {
      this.active = newSelectedOption;
    },
  },

  mounted() {
    this.onChange();
  },

  methods: {
    /*
     * This is a stand in function that will be refactored out at a later time
     * The goal is to support v-tippy attribute dynamically via additionalAttributes below
     */
    getToolTip(option) {
      return option?.tooltip ?? null;
    },

    additionalAttributes(option) {
      return option?.additionalAttributes ?? null;
    },
    getSwatchUnavailableLabel(option) {
      return option.unavailableLabel ? `, ${option.unavailableLabel}` : ', unavailable';
    },
    onMouseLeave(option) {
      if (this.activeMouseOver === null) return;

      this.activeMouseOver = null;
      this.mouseLeaveCallback?.(option);

      /**
       * Emitted when the mouse-leave occurs.
       */
      this.$emit('color-mouseleave', {...option, radioName: this.radioName});
    },

    onMouseOver(option) {
      const optionKeys = Object.keys(option);
      if (!option || (this.activeMouseOver && option[optionKeys[0]] === this.activeMouseOver[optionKeys[0]])) return;
      this.activeMouseOver = option;
      this.mouseOverCallback?.(option);

      /**
       * Emitted when the mouse-over occurs on a new and not currently selected swatch.
       */
      this.$emit('color-mouseover', {...option, radioName: this.radioName});
    },

    onChange() {
      if (typeof this.changeCallback === 'function') {
        this.changeCallback(this.active);
      }

      /**
       * Emitted when the active swatch is changed.
       */
      this.$emit('color-changed', {...this.active, radioName: this.radioName});
    },

    getOptionId(option, index) {
      if (option?.name) return option.name;
      if (option?.firstColor && option?.secondColor) return `${option.firstColor}-${option.secondColor}`;
      if (option?.firstColor) return option.firstColor;

      return index;
    },
  },
};
</script>

<style lang="scss">
@import 'SwatchGroup.scss';
</style>
